import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { setModal, selectModal } from "../../../redux/modalSlice";
import { useForm, Controller } from 'react-hook-form'
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo, faEye } from '@fortawesome/free-solid-svg-icons'
import Loading from '../../../components/Loading'
import Dropzone from 'react-dropzone'
import { useNavigate } from 'react-router-dom'
import SpecialInstruction from '../../../components/elements/SpecialInstruction'
import { triggerRerender } from '../../../redux/rerenderSlice';
import ViewSensitivePiiButtons from "../../../components/ViewSensitivePiiButtons";

export default function CeaseDesistModal() {
  const modal = useSelector(selectModal);
  const request = useRequest()
  const customerId = modal.options?.customerId
  const [customerLevelData, setCustomerLevelData] = useState(null)
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();
  const [fileCustomer, setFileCustomer] = useState(null)
  const [saving, setSaving] = useState(false)
  const navi = useNavigate()
  const active = modal.is_active && modal.type === 'cease_desist_modal'
  const inFlightCustomerId = useRef(null)

  const resetState = () => {
    setFileCustomer(null)
    setCustomerLevelData(null)
    setSaving(false)
    setIsLoading(true)
  }

  const handleCancel = () => {
    resetState()
    dispatch(setModal({ is_active: false }));
    const pathSegments = window.location.pathname.split('/');
    const moduleName = pathSegments[1];
    setTimeout(() => navi(`/${moduleName}/${customerId}`, { state: { reloadPage: true } }), 1000);
  };

  // This modal stays mounted in the Footer; start every opening clean and drop late responses, so a
  // previous customer's C&D record can't put this customer's form into update mode against its id.
  useEffect(() => {
    if (active && customerId) {
      resetState()
      getData(customerId)
    } else {
      inFlightCustomerId.current = null
    }
  }, [active, customerId]);

  const getData = async (customerId) => {
    inFlightCustomerId.current = customerId
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/cease_and_desist_info`)
    try {
      const response = await request.get()
      if (inFlightCustomerId.current !== customerId) return
      // This quick action now represents only the customer-level C&D (no loan_id).
      const customerLevel = (response?.data?.data ?? []).find((item) => !item.loan_id)
      setCustomerLevelData(customerLevel ?? null)
    } catch (error) {
      if (inFlightCustomerId.current !== customerId) return
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    } finally {
      if (inFlightCustomerId.current === customerId) setIsLoading(false)
    }
  }

  // eslint-disable-next-line no-nested-ternary
  return active ? (
    <div className="modal is-active">
      <div className="modal-background"></div>
      <div className="modal-card is-flex is-flex-direction-column column is-5">
        <header className="modal-card-head py-3">
          <p className="modal-card-title is-capitalized">Cease & Desist</p>
          <button className="delete" aria-label="close" onClick={handleCancel}></button>
        </header>
        <section
          className="modal-card-body modal-card-body pt-1 pb-1"
          style={{ minHeight: '110px', overflow: 'hidden' }}
        >
          <FormComponent
            key="customer_level_cd"
            preExistingData={customerLevelData}
            request={request}
            customerId={customerId}
            dispatch={dispatch}
            isLoading={isLoading}
            handleCancel={handleCancel}
            file={fileCustomer}
            setFile={setFileCustomer}
            setSaving={setSaving}
            saving={saving}
          />
        </section>
        <footer className="modal-card-foot is-justify-content-end py-1">
          {!isLoading && (
            <div className="lox-form__buttons-wrapper has-text-right">
              <button
                type="button"
                className="button is-small has-background-grey has-text-white"
                onClick={handleCancel}
              >Cancel
              </button>
              <button
                onClick={() => setSaving(true)}
                className="button is-link is-small"
                disabled={saving && 'disabled'}
              >{saving ? 'Submitting' : 'Continue'}</button>
            </div>
          )}
        </footer>
      </div>
    </div>

  ) : null;
}

const FormComponent = ({
                         preExistingData,
                         request,
                         customerId,
                         dispatch,
                         isLoading,
                         handleCancel,
                         file,
                         setFile,
                         setSaving,
                         saving,
                       }) => {
  const { handleSubmit, control, formState: { errors, isValid } } = useForm()

  const formId = 'cease_desist_modal_form_customer_level'
  const isCreating = !preExistingData

  const [isActiveCheckbox, setIsActiveCheckbox] = useState(preExistingData?.is_active ? preExistingData?.is_active : 0)

  const onDrop = (file) => setFile(file[0])

  useEffect(() => {
    setIsActiveCheckbox(preExistingData?.is_active ? preExistingData?.is_active : 0)
  }, [preExistingData])

  useEffect(() => {
    if (saving && handleSubmit) {
      if (!isValid) {
        setSaving(false)
      }
      handleSubmit(onSubmit)()
    }
  }, [saving, handleSubmit, isValid])

  const onSubmit = async (formData) => {
      if (isCreating) {
        request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/cease_and_desist`);
      } else {
        request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/cease_and_desist/${preExistingData.id}`);
      }
      try {
        let response;
        delete formData.file;
        if (isCreating) {
          response = await request.post(formData, file)
        } else {
          //updating
          response = await request.modify(formData, file)
        }

        if (response.status === 200 || response.status === 201) {
          setSaving(false)
          handleCancel()
          dispatch(triggerRerender({ subpanelId: 'document' }))
          dispatch(setNotification({ type: 'success', body: response?.data?.message }))
        } else {
          setSaving(false)
          setFile(null)
          dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
        }
      } catch (error) {
        setSaving(false)
        const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
        dispatch(setNotification({ type: 'error', body: errorMessage }))
      }
  };
  return (
    <form id={formId} className="lox-form">
        {(isLoading) ? <Loading /> : (
          <div className="columns mb-0 is-flex-direction-column">
              <div className="column">
                <h3>Use this action when a customer demands us to cease & desist (C&D) and has the legal document for this claim</h3>
              </div>
              <div className="column">
                <h3 className="has-text-danger">IMPORTANT: Use this form ONLY if this C&D is at the customer level, and NOT the loan level. In other words this setup will outlive this existing loan. If this is being used as part of a Bankruptcy or Debt Management Company - DO NOT USE THIS FORM, use those respective Quick Actions instead.</h3>
              </div>
              {!isCreating && (
                <div className="column">
                  <h3>
                    Is Active
                    <span
                      style={{ pointerEvents: 'auto' }}
                      className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-weight-normal`}
                      data-tooltip={'Any of these fields that are filled in will override the customer’s field of the same type throughout the system.'}
                    >
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      className={`pl-1 has-text-link`}
                    />
                  </span>
                  </h3>
                  <label>
                    <Controller
                      name="is_active"
                      control={control}
                      defaultValue={preExistingData.is_active || false}
                      render={({ field }) => (
                        <input
                          {...field}
                          className="checkbox is-small"
                          type="checkbox"
                          checked={field.value}
                          onChange={(e) => {
                            field.onChange(e.target.checked)
                            setIsActiveCheckbox(e.target.checked ? 1 : 0)
                          }}
                        />
                      )}
                    />
                  </label>
                </div>
              )}

            <div className="column">
              <span className="is-size-4 has-text-weight-bold">Supporting Files</span>
            </div>
            {
              preExistingData?.document?.id && (
                <div className="column">
                  <div className="columns is-vcentered">
                    <div className="colmun" style={{ paddingLeft: "0.6rem" }}>
                      <ViewSensitivePiiButtons
                        customerId={customerId}
                        documentId={preExistingData?.document?.id}
                        documentTypeId={preExistingData?.document?.document_type_dropdown_option_id}
                        type={'general_document'}
                        style={{ left: '-10px' }}
                        tooltipPosition={'right'}
                      />
                    </div>
                    <div className="column">
                      <i>{preExistingData.document.file_name}</i>
                    </div>
                  </div>
                </div>
              )
            }
            {
              isCreating && (
                <div className="column">
                    <h3>Cease & Desist
                    </h3>
                    <Dropzone onDrop={onDrop}>
                      {({ getRootProps, getInputProps }) => (
                        <div className="columns is-vcentered">
                          <div className="column is-3">
                            <section className="container">
                              <div
                                {...getRootProps({ className: 'dropzone' })}
                                className="is-flex is-justify-content-center is-size-5 has-text-weight-bold fa-border is-clickable"
                                style={{ borderColor: 'gray', borderStyle: 'dashed', backgroundColor: 'lightgray' }}
                              >
                                <input {...getInputProps()} />
                                <p>Drop File</p>
                              </div>
                            </section>
                          </div>
                          <div className="column" style={{ paddingLeft: 0 }}>
                            <span>{file && file.name}</span>
                          </div>
                        </div>

                      )}
                    </Dropzone>
                    <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                      {errors.file &&
                        <span>{errors.file.message ? errors.file.message : 'Cease & Desist Letter is required'}</span>}
                    </p>
                  </div>
                )
              }
            {
              (!isCreating && isActiveCheckbox === 0) && (
                <SpecialInstruction
                  content={"<span>" +
                    "<span class='has-text-weight-bold'>This will perform the following actions:</span>" + "<br>" +
                    "<span class='pl-2'>• Set cease_and_desists record to inactive (not in force)</span> <br> " +
                    "<span class='pl-2'>• Remove customer from Origination Exclusion List (OEL)</span> <br> " +
                    "<span class='pl-2'>• Allow access to email and phone numbers</span> <br> " +
                    "<span class='pl-2'>• Restore customer table settings in charge of Emails / SMS / Voicemails for Marketing messages</span> <br> " +
                    " </span>"}
                  type="is-danger"
                />
              )
            }
            {
              (isCreating || (isActiveCheckbox === 1)) && (
                <SpecialInstruction
                  content={"<span>" +
                    "<span class='has-text-weight-bold'>This will perform the following actions:</span>" + "<br>" +
                    "<span class='pl-2'>• Create/update record in cease_and_desists table for customer_id</span> <br> " +
                    "<span class='pl-2'>• Add to Origination Exclusion List (OEL)</span> <br> " +
                    "<span class='pl-2'>• Block access to all email and phone numbers</span> <br> " +
                    "<span class='pl-2'>• Block all outgoing Phone Call attempts (overrides customers table settings)</span> <br> " +
                    "<span class='pl-2'>• Block all outgoing Marketing messages via Emails/SMS/Voicemails (overrides customers table settings)</span> <br> " +
                    "<span class='pl-2'>• NOTE: Customer WILL still receive Transactional emails</span> <br> " +
                    " </span>"}
                  type="is-warning"
                />
              )
            }
          </div>
        )}

    </form>
  )
}
